// Weicher Hinweis in den Einladen-Modals, wenn der Talentspeicher voll ist.
// Einladen bleibt möglich, die Ergebnisse neuer Talente sind aber gesperrt,
// bis Platz frei wird. Nur in enforced und erst ab voll, davor und in legacy
// und preview rendert er nichts. Lädt jemand mehrere Talente auf einmal ein
// und nur ein Teil passt noch, sagt er, wie viele. Gezählt wird über
// useLimit, die Query lädt nur in enforced und teilt sich den Cache mit der
// Talentliste.
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { MODES } from '../../features/pricing/entitlements'
import { useEntitlements, useLimit } from '../../features/pricing/hooks'
import { LIMIT_STATES } from '../../features/pricing/limits'
import InfoCallout from '../tailwind/InfoCallout'

const FULL_STATES = [LIMIT_STATES.AT_LIMIT, LIMIT_STATES.OVER_LIMIT]

/**
 * Ob der Hinweis erscheint. pending, unknown und unlimited zeigen nichts.
 *
 * @param {{ mode?: string, enforced?: boolean, state?: string }} params
 * @returns {boolean}
 */
export const shouldShowTalentStorageInviteHint = ({ mode, enforced, state }) =>
  mode === MODES.ENFORCED && !!enforced && FULL_STATES.includes(state)

/**
 * Wie viele von count neuen Talenten außerhalb des Speichers landen, wenn
 * noch Platz ist, aber nicht für alle. 0, wenn alle passen, der Speicher
 * schon voll ist (dann gilt der volle Hinweis) oder nicht gezählt wird.
 *
 * @param {{ mode?: string, enforced?: boolean, state?: string,
 *   remaining?: number|null, count?: number }} params
 * @returns {number}
 */
export const talentStorageOverflow = ({
  mode,
  enforced,
  state,
  remaining,
  count = 1
}) => {
  if (mode !== MODES.ENFORCED || !enforced || FULL_STATES.includes(state)) {
    return 0
  }
  if (!Number.isFinite(remaining) || remaining <= 0) return 0

  return Math.max(0, count - remaining)
}

const TalentStorageInviteHint = ({ className, inviteCount }) => {
  const { t } = useTranslation()
  const { mode } = useEntitlements()
  const usage = useLimit('talentStorage')

  const isFull = shouldShowTalentStorageInviteHint({
    mode,
    enforced: usage.enforced,
    state: usage.state
  })
  const overflow = talentStorageOverflow({
    mode,
    enforced: usage.enforced,
    state: usage.state,
    remaining: usage.remaining,
    count: inviteCount
  })

  if (!isFull && !overflow) return null

  return (
    <div data-talent-storage-invite-hint='' className={className}>
      <InfoCallout tone='warning'>
        {isFull
          ? t('pricing.talent_locked.invite_hint')
          : [
              t('pricing.talent_locked.invite_hint_partial_free', {
                count: usage.remaining
              }),
              t('pricing.talent_locked.invite_hint_partial_locked', {
                count: overflow
              })
            ].join(' ')}
      </InfoCallout>
    </div>
  )
}

TalentStorageInviteHint.propTypes = {
  className: PropTypes.string,
  // Zahl der Talente, die gerade eingeladen werden
  inviteCount: PropTypes.number
}

TalentStorageInviteHint.defaultProps = {
  className: undefined,
  inviteCount: 1
}

export default TalentStorageInviteHint
